import { Archive, AlertCircle, ChevronRight, TrendingDown } from 'lucide-react';

/**
 * InventoryHealthStrip
 *
 * Compact inventory health summary for the overview.
 * Shows low-stock and out-of-stock product count.
 * Clicking opens the full inventory view.
 */
export function InventoryHealthStrip({ inventorySummary, inventory, loading, onInventoryClick }) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
        <div className="h-5 bg-gray-100 rounded w-1/3 mb-3" />
        <div className="flex gap-3">
          <div className="h-16 bg-gray-100 rounded-lg flex-1" />
          <div className="h-16 bg-gray-100 rounded-lg flex-1" />
          <div className="h-16 bg-gray-100 rounded-lg flex-1" />
        </div>
      </div>
    );
  }

  const summary = inventorySummary || { total_products: 0, low_stock_count: 0, out_of_stock_count: 0, threshold: 5 };
  const lowStockProducts = inventory?.filter(p => p.is_low_stock && (p.available_quantity ?? p.total_stock) > 0) ?? [];
  const outOfStockProducts = inventory?.filter(p => (p.available_quantity ?? p.total_stock) === 0) ?? [];

  const healthColor =
    summary.out_of_stock_count > 0 ? 'border-red-200' :
    summary.low_stock_count > 0   ? 'border-amber-200' :
    'border-gray-200';

  return (
    <div className={`bg-white rounded-xl border overflow-hidden ${healthColor}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Archive size={16} className="text-gray-500" />
          <span className="font-montserrat font-bold text-sm text-gray-700">
            Salud del Inventario
          </span>
        </div>
        <button
          onClick={onInventoryClick}
          className="flex items-center gap-1 text-xs text-brand-primary font-montserrat font-semibold hover:underline"
        >
          Ver todo
          <ChevronRight size={12} />
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 divide-x divide-gray-100">
        <StatCell
          label="Productos"
          value={summary.total_products}
          color="text-gray-700"
        />
        <StatCell
          label={`Stock bajo (≤${summary.threshold})`}
          value={summary.low_stock_count}
          color={summary.low_stock_count > 0 ? 'text-amber-600' : 'text-gray-700'}
          icon={summary.low_stock_count > 0 ? <TrendingDown size={14} className="text-amber-500" /> : null}
        />
        <StatCell
          label="Sin stock"
          value={summary.out_of_stock_count}
          color={summary.out_of_stock_count > 0 ? 'text-red-600' : 'text-gray-700'}
          icon={summary.out_of_stock_count > 0 ? <AlertCircle size={14} className="text-red-500" /> : null}
        />
      </div>

      {/* Low stock product list — up to 4 items */}
      {(lowStockProducts.length > 0 || outOfStockProducts.length > 0) && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {/* Out of stock first — most urgent */}
          {outOfStockProducts.slice(0, 2).map(product => (
            <ProductRow
              key={product.product_id}
              product={product}
              type="out"
            />
          ))}
          {/* Then low stock */}
          {lowStockProducts.slice(0, 4 - Math.min(outOfStockProducts.length, 2)).map(product => (
            <ProductRow
              key={product.product_id}
              product={product}
              type="low"
            />
          ))}
          {/* "And X more" link */}
          {(summary.low_stock_count + summary.out_of_stock_count > 4) && (
            <button
              onClick={onInventoryClick}
              className="w-full py-2 text-center text-xs text-brand-primary font-montserrat font-semibold hover:bg-gray-50 transition-colors"
            >
              +{summary.low_stock_count + summary.out_of_stock_count - Math.min(inventory?.length || 0, 4)} productos más con stock bajo →
            </button>
          )}
        </div>
      )}

      {/* All healthy state */}
      {summary.low_stock_count === 0 && summary.out_of_stock_count === 0 && summary.total_products > 0 && (
        <div className="px-4 py-4 text-center">
          <p className="text-sm text-gray-400 font-montserrat">
            ✓ Todo el inventario está en buen nivel
          </p>
        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, color, icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-4 px-2 text-center">
      <div className="flex items-center gap-1 mb-1">
        {icon}
        <span className={`font-montserrat font-extrabold text-2xl leading-none ${color}`}>
          {value}
        </span>
      </div>
      <p className="font-montserrat text-xs text-gray-400 leading-tight">{label}</p>
    </div>
  );
}

function ProductRow({ product, type }) {
  const isOut = type === 'out';
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      {/* Product image or placeholder */}
      {product.primary_image ? (
        <img
          src={product.primary_image}
          alt={product.product_title}
          className="w-8 h-8 rounded object-cover shrink-0 bg-gray-100"
        />
      ) : (
        <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center shrink-0">
          <Archive size={14} className="text-gray-300" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="font-montserrat font-semibold text-sm text-gray-800 truncate">
          {product.product_title}
        </p>
        <p className="font-montserrat text-xs text-gray-400">{product.sku}</p>
      </div>

      <span className={`shrink-0 font-montserrat font-bold text-sm px-2 py-0.5 rounded-full ${
        isOut ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
      }`}>
        {isOut ? 'Sin stock' : `${product.available_quantity ?? product.total_stock} uds`}
      </span>
    </div>
  );
}